import { Inject } from '@odinjs/odin';
import { ActionAPI, BasicFile, DeckAPI, DeckValue, FetchAPI, FieldAPI, FileUploaderAPI, FormAPI, ModalService, PanelAPI, ToastNotificationAPI } from '@philips-internal/oswald-ui-components';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnBeforeSubmit, OnCancel, OnClick, OnDynamicContentRender, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { AbortableSubmitEvent, ClickEvent, MountEvent, RenderEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { BrokerService, HttpService, MessageService, } from '@philips-internal/oswald-ui-core/lib/services';
import { isMobile } from '@philips-internal/oswald-ui-utils';
import React, { ReactElement } from 'react';
import { FragmentNames, UploadFileAliases } from '../../codegen';
import { BS_TASK_LIST_CURRENT_WIZARD_STEP, CTX_KEY_TASK_LIST_FIRST_STEP_RENDERED_NOT_DONE } from '../../constants/application-constants';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { APPLICATION_ROUTES } from '../../constants/application-routes';
import { MyChartSendingDocumentsType as FileTaskType } from '../../constants/my-chart-sending-documents-type';
import TASK_STATUS from '../../constants/task-status';
import TaskItem from '../../interfaces/task-item';
import { FileRuleUtils } from '../../utils/file-rules-utils';
import { TaskModuleUtils } from '../../utils/task-module-utils';
import { TasksUtils } from '../../utils/tasks-utils';
import { showToastNotificationError, showToastNotificationSuccess } from '../../utils/toast-notification-utils';
import UserContext from '../../utils/user-context';
import BaseController from '../BaseController';
import { DYNAMIC_STEP_IDENTIFIER } from './tasks-controller';

interface FormNewDocumentProps {
  files: BasicFile[];
}

interface DeckDocumentlItemProps {
  nrSequence: number;
  path: string;
  read: string;
  savedAt: string;
  status: string;
  type: string;
}

interface DocumentSentProps extends Record<string, Value> {
  filePath: string;
  size: number;
  title: string;
}

enum ContextKeys {
  CURRENT_ROUTE_IS_TASKS = 'current_route_is_tasks',
  UPLOAD_FILE_TASK = 'upload_file_task',
}

@Controller({ component: UploadFileAliases.UPLOAD_FILE_LAYOUT, fragment: FragmentNames.UPLOAD_FILE })
export default class UploadFileController extends BaseController {

  disableCheckAction = true;

  @Inject(UploadFileAliases.FILE_UPLOADER_FILE_UPLOAD_FILE)
    fileUploderAPI!: FileUploaderAPI;

  @Inject(UploadFileAliases.FORM_SEND_FILES)
    formSendFiles!: FormAPI;

  @Inject(UploadFileAliases.FIELD_FILES)
    fieldFileUploaderAPI!: FieldAPI;

  @Inject(UploadFileAliases.FETCH_SENT)
    fetchSent!: FetchAPI;

  @Inject(UploadFileAliases.PANEL_FORM)
    panelFormApi!: PanelAPI;

  @Inject(UploadFileAliases.TOAST_NOTIFICATION)
    toastNotificationBlockMessageApi!: ToastNotificationAPI;

  decksAPI: Map<FileTaskType, DeckAPI> = new Map();

  @OnMount(UploadFileAliases.UPLOAD_FILE_LAYOUT)
  onMountLayout(): void {
    this.setInitContextVariables();

    this.listenerCurrentWizardStepFromParent(this.formSendFiles);
    const initialStepIdNotDone = this.context.get(CTX_KEY_TASK_LIST_FIRST_STEP_RENDERED_NOT_DONE);
    BrokerService.publishToTopic(BS_TASK_LIST_CURRENT_WIZARD_STEP, initialStepIdNotDone);
  }

  private listenerCurrentWizardStepFromParent(formApi: FormAPI): void {
    const fragmentStepId = this.context.get<string>('identifier');
    const uploadFileTask = this.context.get<TaskItem>(ContextKeys.UPLOAD_FILE_TASK);
    TaskModuleUtils.listenerCurrentWizardStepFromParent(formApi, fragmentStepId, uploadFileTask);
  }

  private setInitContextVariables(): void {
    /**
     * This fragment is embedded within the task module.
     * When accessing the fragment via task mode, some different behaviors must be performed.
     * And for this needed this information in context.
     */
    const currentRouteIsTasks = this.historyService.getCurrentRoute() === APPLICATION_ROUTES.TASKS;
    this.context.set({[ContextKeys.CURRENT_ROUTE_IS_TASKS]: currentRouteIsTasks});
  }

  @OnMount(UploadFileAliases.FILE_UPLOADER_FILE_UPLOAD_FILE)
  async onMountFileUploader(event: MountEvent): Promise<void> {
    const fileUploaderApi = event.target as FileUploaderAPI;
    FileRuleUtils.validateFileUploaderRules(fileUploaderApi);
  }

  @OnDynamicContentRender({ target: UploadFileAliases.DECK_SENT })
  onDeckGroupRender(event: RenderEvent): void {
    const { render } = event;
    const deckData = event.data;
    const items: DeckDocumentlItemProps[] = deckData as DeckDocumentlItemProps[];
    const isSent =  items.filter(item=>{
      return item.type === FileTaskType.SENT
    });
    const renderSuggestionDate: () => ReactElement = () => {
      return (
        <div className='c-deck__group-header-default-content'>
          { isSent ? MessageService.translate('patient.tab.document-sent') : MessageService.translate('patient.tab.document-received')}
        </div>
      );
    }
    render(renderSuggestionDate);
  }

  @OnMount({ target: UploadFileAliases.FETCH_SENT})
  handleFetchsLoading(event: MountEvent): void {
    const fetchAPI = event.target as FetchAPI;
    fetchAPI.setParameters({ patientId: UserContext.patientId });
    fetchAPI.forceFetch();
  }

  @OnMount({ target: [
    UploadFileAliases.DECK_SENT
  ] })
  handleDocumentDecksLoading(event: MountEvent): void {
    const deckAPI = event.target as DeckAPI;

    let type;
    if(deckAPI.getAlias() === UploadFileAliases.DECK_SENT){
      type = FileTaskType.SENT;
    }
    if(type != null) {
      this.decksAPI.set(type, deckAPI);
    }
  }

  @OnMount(UploadFileAliases.UPLOAD_FILE_CHECK_ACTION)
  onLayoutMount(event: MountEvent): void {
    const checkActionAPI = event.target as ActionAPI;
    const identifier = this.context.get('identifier') as string;
    const nrSequencia = identifier.split(DYNAMIC_STEP_IDENTIFIER.UPLOAD_FILE.concat('-'))[1];
    const taskList = this.context.get('taskList') as TaskItem[];
    const uploadFileTask = taskList.find(task => task.nrSequencia === Number(nrSequencia));
    this.context.set({[ContextKeys.UPLOAD_FILE_TASK]: uploadFileTask});

    if (uploadFileTask) {
      TasksUtils.setInitialActionStatus({
        checkActionAPI,
        onBlocked: () => {
          this.panelFormApi.setVisible(false);
          this.toastNotificationBlockMessageApi.setVisible(true);
        },
        onCompleted: () => {
          this.disableCheckAction = true;
          this.panelFormApi.setVisible(false);
        },
        onPending: () => {
          this.disableCheckAction = false;
          this.context.set({ nrSequencia });
        },
        taskItem: uploadFileTask,
      });
    }
  }

  @OnMount(UploadFileAliases.FIELD_FILES)
  async handleFieldFileUploaderLoading(event: MountEvent): Promise<void> {
    const fieldUploadAPI = event.target as FieldAPI;
    await fieldUploadAPI.validate();
  }

  @OnClick(UploadFileAliases.UPLOAD_FILE_CHECK_ACTION)
  onClickCheckAction(event: ClickEvent): void {
    if (this.disableCheckAction) return;

    const checkAction = event.target as ActionAPI;
    const identifier = this.context.get('identifier') as string;
    const nrSequencia = this.context.get('nrSequencia');

    HttpService.put(APPLICATION_ENDPOINTS.UPDATE_TASK_STATUS, { nrSequencia }).then(() => {
      TasksUtils.setActionChecked(checkAction, identifier);
      this.disableCheckAction = true;
      this.panelFormApi.setVisible(false);

      const uploadFileTask = this.context.get<TaskItem>(ContextKeys.UPLOAD_FILE_TASK);
      this.updateTaskStatusOnContext(uploadFileTask);
    });
  }

  private updateTaskStatusOnContext(uploadFileTask: TaskItem): void {
    uploadFileTask.ieStatusTarefa = TASK_STATUS.COMPLETED;
    this.context.set({[ContextKeys.UPLOAD_FILE_TASK]: uploadFileTask});
  }

  @OnBeforeSubmit({ target: UploadFileAliases.FORM_SEND_FILES })
  async handleFormNewDocumentSubmit(event: AbortableSubmitEvent): Promise<void> {
    if (!(await this.formSendFiles.validate())) {
      event.abort();
      return;
    }

    this.formSendFiles.setLoading(true);

    const { files } = event.record as FormNewDocumentProps;
    const body = files.map<DocumentSentProps>(file => ({
      filePath: file.filepath,
      size: file.size,
      title: file.description || ''
    })) as unknown as Record<string, Value>;

    HttpService.post(APPLICATION_ENDPOINTS.POST_TASKS_SENDING_DOCUMENTS, { patientId: UserContext.patientId }, { body })
      .then(() => {
        this.clearFormNewDocument();
        showToastNotificationSuccess('patient.message.files-uploaded-successfully');
      })
      .catch(() => {
        showToastNotificationError('newaccount.error.internal.server.error.try.again');
      })
      .finally(() => {
        this.formSendFiles.setLoading(false);
        this.fetchSent.forceFetch();
      });
  }

  @OnClick({ target: [
    UploadFileAliases.MY_CHART_FILE_LIST_ITEM_CARD_SENT
  ] })
  handleClickDocumentCard(event: ClickEvent): void {
    const item = event.value as DeckDocumentlItemProps;
    if (!item || item.type === FileTaskType.RECEIVED) {
      return;
    }

    ModalService.openDialog({
      className: isMobile() ? '' : 'my-chart-sending-documents__dialog-modal',
      contextData: { filePath: item.path },
      fragment: 'my-chart-view-sent-document',
      onClose: () => this.markDocumentAsRead(item),
    });
  }

  private markDocumentAsRead(document: DeckDocumentlItemProps): void {
    if (document.read === 'S') {
      return;
    }

    const bodyContent = {
      documentType: document.type,
      nrSequence: document.nrSequence,
    }

    HttpService.put(
      APPLICATION_ENDPOINTS.PUT_MC_READ_DOCUMENT,
      { patientId: UserContext.patientId },
      { body: bodyContent }
    ).then(() => {
      this.updateItemOnDeck(document);
    });
  }

  @OnCancel({ target: UploadFileAliases.FORM_SEND_FILES })
  onCancelForm(): void {
    this.formSendFiles.setEditing(false);
  }

  private updateItemOnDeck(item: DeckDocumentlItemProps): void {
    const deckAPI = this.decksAPI.get(item.type as FileTaskType);
    if (!deckAPI) {
      return;
    }

    const currentDeckData = deckAPI.getData() as unknown as DeckDocumentlItemProps[];
    const itemIndex = currentDeckData.findIndex(document => document.nrSequence === item.nrSequence);
    if (itemIndex === -1) {
      return;
    }

    currentDeckData[itemIndex].read = 'S';
    deckAPI.setData(currentDeckData as unknown as DeckValue[]);
  }

  private clearFormNewDocument(): void {
    this.executeManualFormCancellation();
    setTimeout(() => {
      this.formSendFiles.setEditing(true);
    });
  }

  private executeManualFormCancellation(): void {
    this.formSendFiles.cancel();
  }

}
